import { APPConfig } from 'config/appConfig'
import {
  getMonthStringList,
  i18nTranslate,
  formatDate,
  getTimeZone,
  enableTimeZoneDateConversion,
  convertToBoolean,
  getLocalStorage,
} from 'src/utils'
import { isFlexibleSubscriptionFlagEnabled } from './DatePickerV2Deps'
import {
  multipleSubscriptionContainer,
  cartContainer,
  storeContainer,
  customerContainer,
} from 'src/models'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { getSubscriptionLimitConfig } from 'src/utils/multipleSubscriptionUtils'

const isMultipleSubscriptionFlagEnabled = () => {
  return APPConfig?.getAppConfig()?.enableMultipleSubscription === 'true'
}

const enableSameSkuForFlexibleSubscription = () => {
  return (
    convertToBoolean(
      storeContainer.getStoreConfigProperty('subscriptionsConfig')
        ?.enableSameSkuForFlexibleSubscription
    ) || false
  )
}

const isSubscriptionFlagEnabled = () => {
  const isFlexibleSubscriptionEnabled =
    isFlexibleSubscriptionFlagEnabled() || false
  const isMultipleSubscriptionEnabled =
    isMultipleSubscriptionFlagEnabled() || false
  const isToShowNewUIDesign =
    isFlexibleSubscriptionEnabled || isMultipleSubscriptionEnabled

  return isToShowNewUIDesign
}

const isToShowMultipleSubscriptionOptionsInCart = () => {
  const isMultipleSubscriptionEnabled =
    isMultipleSubscriptionFlagEnabled() || false
  const isUserHasSubscriptionProductInCart =
    cartContainer.hasSubscriptionProduct() || false
  const subscriptionList =
    multipleSubscriptionContainer?.allSubscriptionListResponse?.subscriptions ||
    []
  const isUserHasExistingSubscription = subscriptionList?.length > 0

  return (
    isMultipleSubscriptionEnabled &&
    isUserHasSubscriptionProductInCart &&
    isUserHasExistingSubscription
  )

  // return false
}

const getProcessingDateDetails = props => {
  const { processingDay: orderProcessingDate = 1, nextOrderCreationDate = '' } =
    props

  let enableTimeZoneDateFormat = enableTimeZoneDateConversion() || false

  let currentMonth, currentYear, currentDay
  let nextProcessingMonth, nextProcessingYear

  if (enableTimeZoneDateFormat) {
    // 1-based months (formatDate returns '01'–'12')
    const currentDate = Date.now()
    currentMonth = Number.parseInt(
      formatDate(currentDate, { month: '2-digit' }),
      10
    )
    currentYear = Number.parseInt(
      formatDate(currentDate, { year: 'numeric' }),
      10
    )
    currentDay = Number.parseInt(
      formatDate(currentDate, { day: '2-digit' }),
      10
    )
    nextProcessingMonth = Number.parseInt(
      formatDate(nextOrderCreationDate, { month: '2-digit' }),
      10
    )
    nextProcessingYear = Number.parseInt(
      formatDate(nextOrderCreationDate, { year: 'numeric' }),
      10
    )
  } else {
    // 0-based months (getMonth() returns 0–11)
    const currentDate = new Date()
    currentMonth = currentDate.getMonth()
    currentYear = currentDate.getFullYear()
    currentDay = currentDate.getDate()
    const nextDate = new Date(nextOrderCreationDate)
    nextProcessingMonth = nextDate.getMonth()
    nextProcessingYear = nextDate.getFullYear()
  }

  const nextOrderAbsMonth = nextProcessingYear * 12 + nextProcessingMonth
  const currentAbsMonth = currentYear * 12 + currentMonth

  let processingMonth
  let processingYear

  if (nextOrderAbsMonth > currentAbsMonth) {
    // nextOrderCreationDate is in a future month — trust it
    processingMonth = nextProcessingMonth
    processingYear = nextProcessingYear
  } else if (nextOrderAbsMonth === currentAbsMonth) {
    // Next order is in current month
    if (orderProcessingDate < currentDay) {
      // Processing day already passed — move to next month
      processingMonth = enableTimeZoneDateFormat
        ? (currentMonth % 12) + 1
        : (currentMonth + 1) % 12
      processingYear =
        processingMonth < currentMonth ? currentYear + 1 : currentYear
    } else {
      processingMonth = currentMonth
      processingYear = currentYear
    }
  } else {
    // nextOrderCreationDate is in the past — fallback
    if (orderProcessingDate >= currentDay) {
      processingMonth = currentMonth
      processingYear = currentYear
    } else {
      processingMonth = enableTimeZoneDateFormat
        ? (currentMonth % 12) + 1
        : (currentMonth + 1) % 12
      processingYear =
        processingMonth < currentMonth ? currentYear + 1 : currentYear
    }
  }

  const orderProcessingDay = new Date(
    processingYear,
    processingMonth,
    orderProcessingDate
  )

  let orderProcessingFullDate =
    getMonthStringList()[orderProcessingDay?.getMonth()] +
    ' ' +
    orderProcessingDate +
    ', ' +
    orderProcessingDay?.getFullYear()

  if (enableTimeZoneDateFormat) {
    orderProcessingFullDate =
      getMonthStringList()[processingMonth - 1] +
      ' ' +
      orderProcessingDate +
      ', ' +
      processingYear
  }

  return {
    orderProcessDate: orderProcessingFullDate,
    orderProcessDay: orderProcessingDate,
  }
}

const capitalizeFirstLetters = (subscriptionName = '') => {
  return subscriptionName
    ?.split?.(' ')
    ?.map(word => {
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)
    })
    ?.join(' ')
}

const getValidationErrorMessage = (validateOriginalAddressResponse = {}) => {
  const errorMessage = validateOriginalAddressResponse?.message || ''
  const errorCode = validateOriginalAddressResponse?.code || ''

  const inputValidationFailedError = i18nTranslate(
    'address.validationMsgToast',
    'Please correct the address to proceed.'
  )
  const invalidAddressError = i18nTranslate(
    'address.invalidAddress',
    'Address provided is invalid'
  )

  let errorMessageToDisplay

  if (errorMessage === 'input.validation.failed') {
    errorMessageToDisplay = inputValidationFailedError
  } else if (errorCode === 'ONESOURCE_ADPLGIN_VAL_ERROR') {
    errorMessageToDisplay = invalidAddressError
  } else {
    errorMessageToDisplay = errorMessage
  }

  return errorMessageToDisplay
}

const getTransformedBillingAddress = (billingAddress = {}) => {
  const {
    street1 = '',
    addressLine1 = '',
    street2 = '',
    addressLine2 = '',
    state = '',
    stateList = '',
    zipCode = '',
    zip = '',
  } = billingAddress

  delete billingAddress?.validated
  delete billingAddress?.validatedOn

  const transformedBillingAddress = {
    ...billingAddress,
    addressLine1: street1 || addressLine1 || '',
    addressLine2: street2 || addressLine2 || '',
    stateList: state || stateList || '',
    zip: zipCode || zip || '',
  }
  return transformedBillingAddress
}

const getFormattedDateValue = (date, type) => {
  return date?.find(part => part?.type === type)?.value
}

const getSubscriptionProcessingDate = timestamp => {
  const currentDate = typeof timestamp === 'number' ? timestamp : Date.now()
  const curLocale =
    getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
    }) || 'en_US'
  const locale = curLocale.replace('_', '-') || 'en-US'
  const timeZone = getTimeZone() || 'US/Mountain'
  const formattedDate = new Intl.DateTimeFormat(locale, {
    timeZone: timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).formatToParts(currentDate)

  const weekday = getFormattedDateValue(formattedDate, 'weekday')
  const month = getFormattedDateValue(formattedDate, 'month')
  const day = getFormattedDateValue(formattedDate, 'day')

  const formatted = `${weekday}, ${month} ${day}`

  return formatted
}

/**
 * Checks if subscription is restricted for the current user's account type
 * based on the `restrictedAccountTypes` key inside the `subscriptionsConfig`
 * store property.
 *
 * Vietnam market restricts subscription access to Members only.
 * BA-I (Brand Affiliate) and BA-BE (Brand Affiliate-Business Entity) are blocked.
 *
 * @returns {boolean} true if the current account type is in the restricted list
 */
const isSubscriptionRestrictedForAccountType = () => {
  try {
    const subscriptionsConfig = getSubscriptionLimitConfig() || {}
    const { restrictedAccountTypes } = subscriptionsConfig

    if (
      !Array.isArray(restrictedAccountTypes) ||
      restrictedAccountTypes.length === 0
    ) {
      return false
    }

    // restrictedAccountTypes is configured, so hide subscription until the
    // profile call completes to avoid showing it to a restricted account type.
    if (!customerContainer?.isProfileCallOver) {
      return true
    }

    const accountType =
      customerContainer?.profileResponse?.accounts?.type ||
      getLocalStorage('accountType') ||
      ''

    if (!accountType) return false

    return restrictedAccountTypes.some(
      restrictedType =>
        accountType?.toLowerCase()?.trim() ===
        restrictedType?.toLowerCase()?.trim()
    )
  } catch (e) {
    console.error('Error checking subscription restriction for account type', e)
    return false
  }
}

export {
  isMultipleSubscriptionFlagEnabled,
  enableSameSkuForFlexibleSubscription,
  isSubscriptionFlagEnabled,
  isToShowMultipleSubscriptionOptionsInCart,
  getProcessingDateDetails,
  capitalizeFirstLetters,
  getValidationErrorMessage,
  getTransformedBillingAddress,
  getSubscriptionProcessingDate,
  isSubscriptionRestrictedForAccountType,
}
